import { useWidgetData } from "../Contexts/dashboardHooks";
import { resolveDateRange } from "../Utils/resolveDateRange";
import { WidgetInfoPopoverContent, type WidgetInfoRow } from "../WidgetComponents/WidgetInfoPopover";
import { selectionAggWord, selectionSeriesLabel } from "../WidgetComponents/buildScatterplotOptions";
import type { ScatterplotAxisDef, ScatterplotComponentSpec } from "../Processing/types";

const AGG_LABELS: Record<string, string> = { avg: "Average", sum: "Sum", best: "Best" };
const GROUP_LABELS: Record<string, string> = { player: "Player", position: "Position", drill: "Drill" };

export function ScatterplotInfo({ id }: { id: string }) {
  const data = useWidgetData(id);
  if (!data || data.type !== "scatterplot") return null;
  const spec = data.spec as ScatterplotComponentSpec;
  const misc = spec.scatterplot_builder_misc;
  const mutations = spec.widgetMutations;

  const names = misc.selectedSubjectNames ?? [];
  const subjects = names.length > 0 ? names.join(", ") : "Full Team";
  const dates = resolveDateRange(misc);
  const groupByFor = (agg: string) =>
    mutations.teamAverages
      ? `${selectionSeriesLabel(misc, selectionAggWord(null, agg))} per Day`
      : `${GROUP_LABELS[misc.groupBy ?? "player"] ?? "Player"} per Day`;

  const extras: string[] = [];
  if (mutations.groupByGameDate) extras.push("Grouped by Game Date");
  if (mutations.showTrendline) {
    extras.push(mutations.trendlineScope === "series" ? "Trendline per Series" : "Trendline");
  }
  if (mutations.showPointLabels) {
    extras.push(mutations.showPointDates ? "Point Labels with Dates" : "Point Labels");
  }

  const axisRow = (axis: ScatterplotAxisDef, name: string, withExtras: boolean): WidgetInfoRow => ({
    queryId: axis.queryId,
    label: `${name}: ${axis.displayNameOverride || axis.displayName}`,
    aggLabel: AGG_LABELS[axis.metricAgg] ?? axis.metricAgg,
    dates,
    subjects,
    groupBy: groupByFor(axis.metricAgg),
    extras: withExtras ? extras : [],
  });

  const rows: WidgetInfoRow[] = [];
  if (spec.xAxisMode === "date") {
    rows.push({
      queryId: "x-date",
      label: "X: Date",
      aggLabel: "—",
      dates,
      subjects,
      groupBy: groupByFor(spec.yAxis.metricAgg),
      extras: [],
    });
  } else if (spec.xAxis) {
    rows.push(axisRow(spec.xAxis, "X", false));
  }
  rows.push(axisRow(spec.yAxis, "Y", true));

  return <WidgetInfoPopoverContent rows={rows} />;
}
